import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { HolidaysToolbox } from '../components/holidays/holidaysToolbox';
import { AppCard } from '../components/app/appCard';
import { AppMain } from '../layout/appMain';
import { DaysOff } from '../models/admin';
import { format } from 'date-fns';
import { AutocompleteOption } from '../models/autocompleteOption';
import {
  addDayOff,
  deleteDayOff,
  editDayOff,
  generateDaysOff,
  getHolidays,
} from '../services/api/holidayService';
import EditHolidayDialog from '../components/holidays/editHolidayDialog';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { OnEditParams } from '../models/holidays';
import { FieldValues } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectProfile } from '../store/profileSlice';
import { AppConfirmationDelete } from '../components/app/confirmation/appConfiramationDelete';
import AddHolidayDialog from '../components/holidays/addHolidayDialog';
import { COUNTRY_OPTIONS, YEAR_OPTIONS } from '../constants';
import AppLoading from '../components/app/appLoading';
import { AppConfirmationSuccessful } from '../components/app/confirmation/appConfirmationSuccessful';
import useAppSnackbar from '../hooks/useAppSnackbar';
import { useUserRoles } from '../hooks/useUserRoles';
import { AxiosError } from 'axios';
import { formatActivityDate } from '../services/dateService';
import {
   formatDateOnly,
} from '../services/dateService';


const style = {
  box: {
    mt: 4,
    display: 'flex',
    flexWrap: 'wrap',
  },
} as const;

export function PageHolidays(): JSX.Element {
  const profile = useSelector(selectProfile);
  const currentUserCountry = COUNTRY_OPTIONS.find(
    (option) => option.label === profile?.location
  );

  const { isAdmin } = useUserRoles();
  const [holidays, setHolidays] = useState<DaysOff[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<AutocompleteOption>(
    currentUserCountry || COUNTRY_OPTIONS[0]
  );
  const [selectedYear, setSelectedYear] = useState<AutocompleteOption>(
    YEAR_OPTIONS[0]
  );
  const { onErrorSnackbar } = useAppSnackbar();
  const [openSuccessDialog, setOpenSuccessDialog] = useState({
    isOpen: false,
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [openEditHolidayDialog, setOpenEditHolidayDialog] = useState(false);
  const [openDeleteHolidayDialog, setOpenDeleteHolidayDialog] = useState(false);
  const [openAddHolidayDialog, setOpenAddHolidayDialog] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<
    OnEditParams | undefined
  >();

  const getDaysOffWithParams = useCallback(async () => {
    const holidaysList = await getHolidays({
      country: selectedCountry.label,
      filterDate: parseInt(selectedYear.label),
    });
    const filteredHolidays = holidaysList.sort((a, b) =>
      a.dayOffDate > b.dayOffDate ? 1 : -1
    );
    setHolidays(filteredHolidays);
  }, [selectedCountry, selectedYear]);

  useEffect(() => {
    const loadHolidays = async () => {
      setIsLoading(true);
      try {
        await getDaysOffWithParams();
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        const typedError = error as Error | AxiosError;
        onErrorSnackbar(typedError, "Holidays couldn't be loaded!");
      }
    };

    loadHolidays();
  }, [getDaysOffWithParams, onErrorSnackbar]);

  const onDelete = useCallback((holiday: DaysOff) => {
    const { id, dayOffDate, name } = holiday;

    return () => {
      setOpenDeleteHolidayDialog(true);
      setSelectedHoliday({ id, dayOffDate, name });
    };
  }, []);

  const onClose = useCallback(() => {
    setOpenEditHolidayDialog(false);
    setOpenDeleteHolidayDialog(false);
    setOpenSuccessDialog({ isOpen: false, message: '' });
    setOpenAddHolidayDialog(false);
    setSelectedHoliday(undefined);
  }, []);

  const handleDeleteHoliday = useCallback(() => {
    if (selectedHoliday) {
      deleteDayOff(selectedHoliday.id)
        .then(() => {
          getDaysOffWithParams()
            .then(() => {
              setOpenDeleteHolidayDialog(false);
              setOpenSuccessDialog({
                isOpen: true,
                message: 'Successfully deleted',
              });
            })
            .then(() => {
              setTimeout(() => {
                onClose();
              }, 1000);
            });
        })
        .catch((err) => {
          onErrorSnackbar(err, 'Deletion was unsuccessful!');
        });
    }
  }, [selectedHoliday, getDaysOffWithParams, onErrorSnackbar]);

  const onEdit = useCallback((holiday: DaysOff) => {
    const { id, dayOffDate, name } = holiday;

    return () => {
      setOpenEditHolidayDialog(true);
      setSelectedHoliday({ id, dayOffDate, name });
    };
  }, []);

  const handleEditHoliday = useCallback(
    (params: FieldValues) => {
      setIsLoading(true);

      const { id, dayOffDate, name } = params;

      const formattedDate = formatActivityDate(dayOffDate);

      editDayOff({ id, name, dayOffDate: formattedDate })
        .then(() => {
          getDaysOffWithParams()
            .then(() => {
              setOpenEditHolidayDialog(false);
              setOpenSuccessDialog({
                isOpen: true,
                message: 'Changes successfully saved',
              });
              setIsLoading(false);
            })
            .then(() => {
              setTimeout(() => {
                onClose();
              }, 1000);
            });
        })
        .catch((err) => {
          onErrorSnackbar(err, 'Saving was unsuccessful!');
          setIsLoading(false);
        });
    },
    [getDaysOffWithParams, onClose, onErrorSnackbar]
  );

  const handleGenerateHolidays = useCallback(() => {
    setIsLoading(true);
    generateDaysOff()
      .then(() => {
        getDaysOffWithParams()
          .then(() => {
            setOpenSuccessDialog({
              isOpen: true,
              message: 'Holidays generated successfully',
            });
            setIsLoading(false);
          })
          .then(() => {
            setTimeout(() => {
              onClose();
            }, 1000);
          });
      })
      .catch((err) => {
        onErrorSnackbar(err, "Holidays couldn't be generated!");
      });
  }, [getDaysOffWithParams, onErrorSnackbar]);

  const onAdd = useCallback(() => {
    setOpenAddHolidayDialog(true);
  }, []);

  const handleAddHoliday = useCallback(
    (params: FieldValues) => {
      const { name, dayOffDate, country, isDateFixed } = params;
      const formattedDate = formatActivityDate(dayOffDate);
      setIsLoading(true);
      addDayOff({
        name,
        isDateFixed,
        country: country.label,
        dayOffDate: formattedDate,
      })
        .then(() => {
          getDaysOffWithParams()
            .then(() => {
              setOpenAddHolidayDialog(false);
              setOpenSuccessDialog({
                isOpen: true,
                message: 'Holiday added successfully',
              });
              setIsLoading(false);
            })
            .then(() => {
              setTimeout(() => {
                onClose();
              }, 1000);
            });
        })
        .catch((err) => {
          onErrorSnackbar(err, "Holiday couldn't be added!");
          setIsLoading(false);
        });
    },
    [getDaysOffWithParams, onClose, onErrorSnackbar]
  );

  const headerButtons = useMemo(() => {
    if (isAdmin) {
      return [
        { icon: FileDownloadOutlinedIcon, onClick: handleGenerateHolidays },
        { icon: AddIcon, onClick: onAdd },
      ];
    }

    return [];
  }, [isAdmin, handleGenerateHolidays, onAdd]);

  const handleChangeCountry = useCallback((e: React.BaseSyntheticEvent) => {
    const country = COUNTRY_OPTIONS.find(
      (option) => option.label === e.target.textContent
    );

    if (country) {
      setSelectedCountry(country);
    }
  }, []);

  const handleChangeYear = useCallback((e: React.BaseSyntheticEvent) => {
    const year = YEAR_OPTIONS.find(
      (option) => option.label === e.target.textContent
    );

    if (year) {
      setSelectedYear(year);
    }
  }, []);

  return (
    <AppMain buttons={headerButtons}>
      {selectedHoliday && (
        <EditHolidayDialog
          isOpen={openEditHolidayDialog}
          handleClose={onClose}
          handleEditHoliday={handleEditHoliday}
          selectedHoliday={selectedHoliday}
        />
      )}

      <AddHolidayDialog
        isOpen={openAddHolidayDialog}
        handleClose={onClose}
        handleAddHoliday={handleAddHoliday}
      />

      {selectedHoliday && (
        <AppConfirmationDelete
          isOpen={openDeleteHolidayDialog}
          message={`Delete holiday ${selectedHoliday.name}?`}
          onClose={onClose}
          onConfirm={handleDeleteHoliday}
        />
      )}

      <AppConfirmationSuccessful
        isOpen={openSuccessDialog.isOpen}
        message={openSuccessDialog.message}
        onClose={onClose}
      />

      <HolidaysToolbox
        countries={COUNTRY_OPTIONS}
        selectedCountry={selectedCountry}
        yearOptions={YEAR_OPTIONS}
        selectedYear={selectedYear}
        handleChangeCountry={handleChangeCountry}
        handleChangeYear={handleChangeYear}
        handleGenerateHolidays={handleGenerateHolidays}
        onAdd={onAdd}
        isAdmin={isAdmin}
      />
      {isLoading && <AppLoading />}
      {!isLoading && (
        <Box>
          <Typography variant="pageTitle" fontSize={'28px'} marginLeft={4}>
            {`Holidays in ${selectedCountry.label}`}
          </Typography>
          <Box sx={style.box}>
            {holidays.length === 0 ? (
              <Typography variant="h4" marginLeft={4}>
                {`${selectedCountry.label} has no holidays scheduled for ${selectedYear.label}.`}
              </Typography>
            ) : (
              holidays.map((holiday) => (
                <AppCard
                  key={holiday.id}
                  title={
                    holiday.dayOffDate === null
                      ? 'N/A'
                      : format(new Date(formatDateOnly(holiday.dayOffDate)), 'MMMM d, yyyy')
                  }
                  statusInfo={
                    holiday.dayOffDate === null
                      ? ''
                      : format(new Date(formatDateOnly(holiday.dayOffDate)), 'eeee')
                  }
                  details={[holiday.name]}
                  buttons={
                    isAdmin
                      ? [
                          {
                            label: 'Delete',
                            onClick: onDelete(holiday),
                          },
                          {
                            label: 'Edit',
                            onClick: onEdit(holiday),
                            isPrimary: true,
                          },
                        ]
                      : []
                  }
                />
              ))
            )}
          </Box>
        </Box>
      )}
    </AppMain>
  );
}
